import React, { useCallback, useEffect, useState } from 'react'
import { Card, Table, Form, Spinner } from '@themesberg/react-bootstrap'
import { getChannelsByRegion, updateChannelWeekdays, toggleChannelHasLoto } from '../../../../controller/api/__loto__/channel';
import { useCookies } from 'react-cookie';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const WEEKDAY_LABELS = {
  Mon: 'T2',
  Tue: 'T3',
  Wed: 'T4',
  Thu: 'T5',
  Fri: 'T6',
  Sat: 'T7',
  Sun: 'CN',
}

const REGION_LABELS = {
  MN: 'Miền Nam',
  MT: 'Miền Trung',
  MB: 'Miền Bắc',
}

const TableRow = ({ id, name, weekdays, hasLoto, onWeekdayToggle, onHasLotoToggle, saving }) => {
  return (
    <tr>
      <td>
        <strong className="text-danger">{id}</strong>
      </td>
      <td>{name || ''}</td>
      <td className="text-center">
        <Form.Check
          type="switch"
          checked={hasLoto === 1}
          onChange={(e) => onHasLotoToggle(id, e.target.checked ? 1 : 0)}
          disabled={saving}
        />
      </td>
      {WEEKDAYS.map((day) => (
        <td key={day} className="text-center">
          <Form.Check
            type="checkbox"
            checked={weekdays.includes(day)}
            onChange={() => onWeekdayToggle(id, day)}
            disabled={saving || hasLoto !== 1}
          />
        </td>
      ))}
    </tr>
  )
}

export const ChannelTable = ({ region }) => {
  const [channels, setChannels] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [cookies] = useCookies(['lottmekong'])

  const fetchChannels = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getChannelsByRegion(cookies.lottmekong, region)
      if (res && res.data) {
        setChannels(res.data)
      }
    } catch (err) {
      console.error('fetchChannels error:', err)
    } finally {
      setLoading(false)
    }
  }, [cookies.lottmekong, region])

  useEffect(() => {
    fetchChannels()
  }, [fetchChannels])

  const onWeekdayToggle = async (channelId, day) => {
    const channel = channels.find((c) => c.id === channelId)
    if (!channel) return

    const currentWeekdays = channel.weekdays || []
    const newWeekdays = currentWeekdays.includes(day)
      ? currentWeekdays.filter((d) => d !== day)
      : [...currentWeekdays, day]

    // Optimistic update
    setChannels((prev) =>
      prev.map((c) => (c.id === channelId ? { ...c, weekdays: newWeekdays } : c))
    )

    setSaving(true)
    try {
      const resp = await updateChannelWeekdays(cookies.lottmekong, channelId, newWeekdays)
      if (!resp) {
        // Revert on failure
        setChannels((prev) =>
          prev.map((c) => (c.id === channelId ? { ...c, weekdays: currentWeekdays } : c))
        )
        alert('Cập nhật lịch quay thất bại')
      }
    } catch (err) {
      setChannels((prev) =>
        prev.map((c) => (c.id === channelId ? { ...c, weekdays: currentWeekdays } : c))
      )
      alert('Cập nhật lịch quay thất bại')
    } finally {
      setSaving(false)
    }
  }

  const onHasLotoToggle = async (channelId, newHasLoto) => {
    const channel = channels.find((c) => c.id === channelId)
    if (!channel) return

    const oldHasLoto = channel.hasLoto

    // Optimistic update
    setChannels((prev) =>
      prev.map((c) => (c.id === channelId ? { ...c, hasLoto: newHasLoto } : c))
    )

    setSaving(true)
    try {
      const resp = await toggleChannelHasLoto(cookies.lottmekong, channelId, newHasLoto)
      if (!resp) {
        setChannels((prev) =>
          prev.map((c) => (c.id === channelId ? { ...c, hasLoto: oldHasLoto } : c))
        )
        alert('Cập nhật trạng thái Lô Tô thất bại')
      }
    } catch (err) {
      setChannels((prev) =>
        prev.map((c) => (c.id === channelId ? { ...c, hasLoto: oldHasLoto } : c))
      )
      alert('Cập nhật trạng thái Lô Tô thất bại')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card border="light" className="shadow-sm">
      <Card.Header>
        <h5>Gán đài Lô Tô theo thứ - {REGION_LABELS[region] || region}</h5>
        <small className="text-muted">
          Bật "Có Lô Tô" để cho phép mua Lô Tô cho đài. Chọn các ngày trong tuần mà đài quay số.
        </small>
      </Card.Header>
      <Table responsive className="align-items-center table-flush">
        <thead className="thead-light">
          <tr>
            <th scope="col">Mã</th>
            <th scope="col">Tên đài</th>
            <th scope="col" className="text-center">Có Lô Tô</th>
            {WEEKDAYS.map((day) => (
              <th key={day} scope="col" className="text-center">
                {WEEKDAY_LABELS[day]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={10} className="text-center py-4">
                <Spinner animation="border" size="sm" className="me-2" />
                Đang tải...
              </td>
            </tr>
          ) : channels.length === 0 ? (
            <tr>
              <td colSpan={10} className="text-center py-4 text-muted">
                Không có đài nào
              </td>
            </tr>
          ) : (
            channels.map((ch) => (
              <TableRow
                key={ch.id}
                {...ch}
                onWeekdayToggle={onWeekdayToggle}
                onHasLotoToggle={onHasLotoToggle}
                saving={saving}
              />
            ))
          )}
        </tbody>
      </Table>
    </Card>
  )
}